import React, { useEffect, useMemo } from "react";
import { graphql, usePaginationFragment } from "react-relay";
import StatTable from "../../../../../../StatTable";
import ScrollShadow from "../../../__shared/NFLTable";
import {
  buildStatEngineColumns,
  filterRows,
  toStatRow,
  zeroCellProps,
} from "./statEngineRows";
import { makePlayerCell } from "./PlayerCell";

const StatTablePagination = graphql`
  fragment StatEngineTable_query on Query
  @argumentDefinitions(
    count: { type: Int, defaultValue: 1000 }
    cursor: { type: String, defaultValue: null }
    startWeek: { type: Int, defaultValue: 1 }
    endWeek: { type: Int, defaultValue: 20 }
    positions: { type: "[String]", defaultValue: null }
    sortBy: { type: "NFLStatSortByInput", defaultValue: null }
    weeks: { type: "[Int]", defaultValue: null }
    seasonID: { type: "ID", defaultValue: null }
  )
  @refetchable(queryName: "NFLStatEnginePaginationQuery") {
    nflPlayerWeekStats(
      startWeek: $startWeek
      endWeek: $endWeek
      positions: $positions
      sortBy: $sortBy
      weeks: $weeks
      seasonId: $seasonID
    ) {
      headers {
        Header
        accessor
      }
      rows(first: $count, after: $cursor) @connection(key: "StatTable_rows") {
        edges {
          node {
            player {
              firstName
              lastName
              position {
                name
              }
              dkPlayer {
                id
              }

              injuryStatus

              team {
                id
                abbreviation
              }
            }
            stats
          }
        }
      }
    }
  }
`;

const INITIAL_STATE = { sortBy: [{ id: "totMkt", desc: true }] };

export default function StatEngineTable({
  query,
  positions,
  teamIds,
  teams,
  onRowCount,
  emptyState,
}) {
  const { data } = usePaginationFragment(StatTablePagination, query);
  const stats = data.nflPlayerWeekStats;

  if (!stats) {
    throw new Error("nflPlayerWeekStats returned null");
  }

  const allRows = useMemo(
    () => stats.rows.edges.map((edge) => toStatRow(edge.node)),
    [stats]
  );

  const rows = useMemo(
    () => filterRows(allRows, { positions, teamIds }),
    [allRows, positions, teamIds]
  );

  const PlayerCell = useMemo(() => {
    const logos = {};
    teams.forEach((team) => {
      logos[team.abbreviation] = team.logoSrc;
    });
    return makePlayerCell(logos);
  }, [teams]);

  const columns = useMemo(
    () => buildStatEngineColumns(stats.headers, rows, PlayerCell),
    [stats.headers, rows, PlayerCell]
  );

  useEffect(() => {
    onRowCount(rows.length);
  }, [rows.length, onRowCount]);

  if (rows.length === 0) {
    return emptyState;
  }

  return (
    <ScrollShadow>
      <StatTable
        columns={columns}
        data={rows}
        paginate={true}
        pagination="compact"
        initialState={INITIAL_STATE}
        getCellProps={zeroCellProps}
        showDKPlayerInfo={() => {}}
        interactiveCells={false}
        autoResetSortBy={false}
      />
    </ScrollShadow>
  );
}
